// authConfig.js — Cognito / Microsoft-Entra SSO configuration.
//
// Every value comes from the environment. None are hardcoded, because sitops and
// prodops are not deployed yet and **prodops is eu-west-1** — so even the region
// has to be a variable. Standing up a new environment is then a matter of new
// env values, not a code change.
//
// Backend contract: developer-tool/.claude/plans/frontend-sso-handoff.md
// Plan and decisions: .claude/plans/project-plan-2/project_plan.md
//
// TARGET PATH: src/shared/config/authConfig.js

// ============================================
// Redirect and logout URIs are DERIVED, not configured
// ============================================
// All three registered redirect URIs are "<origin>/callback", so deriving from
// the current origin covers localhost, the custom domain and the CloudFront
// domain with one expression — and it is byte-identical between the authorize
// call and the token exchange by construction, which is the single most common
// cause of redirect_uri_mismatch.
//
// It also avoids a subtler trap. finops.devops.tonicsuite.io and
// d3fgqsr7zmfj8r.cloudfront.net are the SAME devops distribution, but browser
// storage is per-origin: a login begun on one hostname and finished on the other
// would find an empty sessionStorage, fail the `state` check, and report nothing
// useful. Deriving from the live origin makes that impossible.
//
// Cognito matches these exactly, with no wildcards. A new origin is a Terraform
// change in developer-tool-back-end-java — ask, never work around it.
//
// Registered redirect URIs (devops):
//   http://localhost:3000/callback
//   https://finops.devops.tonicsuite.io/callback
//   https://d3fgqsr7zmfj8r.cloudfront.net/callback
//
// Registered logout URIs (devops): the same three origins, with a trailing slash.
const origin = () => window.location.origin;

export const redirectUri = () => `${origin()}/callback`;

// Trailing slash is deliberate — the registered logout URIs carry one, and
// Cognito compares exactly.
export const logoutUri = () => `${origin()}/`;

// ============================================
// Environment values
// ============================================
// devops values live in .env. sitops and prodops follow the same shape with the
// environment name substituted; prodops is eu-west-1. Their real values are not
// invented here — they come from the backend team when those pools exist.
const raw = {
  domain: process.env.REACT_APP_COGNITO_DOMAIN,
  clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  identityProvider: process.env.REACT_APP_COGNITO_IDP,
};

// ============================================
// Derived values
// ============================================
/**
 * The issuer, derived from the user pool ID rather than configured separately.
 *
 * A Cognito pool ID is "<region>_<id>", so the region is already in it. Deriving
 * the issuer means the region cannot drift out of step with the pool — which
 * matters precisely because prodops is eu-west-1 while devops and sitops are
 * us-east-1, and a hardcoded region would work in two environments and fail in
 * the third.
 */
export const issuerFor = (userPoolId) => {
  const region = (userPoolId || '').split('_')[0];
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
};

// ============================================
// Validation
// ============================================
// Fail loudly and early. A missing client ID otherwise surfaces as Cognito
// replying "Client does not exist" after a redirect, which reads like a Cognito
// or backend fault and sends people looking in the wrong place. The handoff calls
// this out specifically. This is also deliberately the opposite of
// axiosConfig's baseURL, which falls back to a hardcoded devops URL and lets a
// misconfigured build silently talk to devops.
const REQUIRED = ['domain', 'clientId', 'userPoolId', 'identityProvider'];

const ENV_NAMES = {
  domain: 'REACT_APP_COGNITO_DOMAIN',
  clientId: 'REACT_APP_COGNITO_CLIENT_ID',
  userPoolId: 'REACT_APP_COGNITO_USER_POOL_ID',
  identityProvider: 'REACT_APP_COGNITO_IDP',
};

/** Names of the env vars that are missing or blank. Empty array when configured. */
export const missingConfig = () =>
  REQUIRED.filter((key) => !String(raw[key] || '').trim()).map((key) => ENV_NAMES[key]);

export const isConfigured = () => missingConfig().length === 0;

/**
 * Throws listing every missing variable at once, rather than failing on the
 * first and hiding the rest — someone setting this up for a new environment
 * should see the whole list in one go.
 */
export const assertConfigured = () => {
  const missing = missingConfig();
  if (missing.length > 0) {
    throw new Error(
      `Cognito SSO is not configured. Missing: ${missing.join(', ')}. ` +
        'Set these in .env (devops values are in .claude/plans/project-plan-2/project_plan.md).'
    );
  }
};

// ============================================
// Rollout flag — Decision 1
// ============================================
/**
 * Whether the username/password form still appears alongside the Microsoft
 * button.
 *
 * Not a preference: sitops and prodops have no Cognito pool yet, so a hard
 * cutover would leave those environments unable to log in at all. The backend
 * keeps POST /v1.0/auth/login alive for the whole rollout, so devops can go
 * Microsoft-only while the others keep the form. It is also the rollback if SSO
 * misbehaves — a flag flip, not a revert and redeploy.
 *
 * Defaults to ON when unset, so an environment that has not been told about SSO
 * keeps working rather than losing its only way in.
 */
export const passwordLoginEnabled = () =>
  String(process.env.REACT_APP_ALLOW_PASSWORD_LOGIN ?? 'true').toLowerCase() !== 'false';

// ============================================
// Public config
// ============================================
export const authConfig = {
  get domain() {
    return raw.domain;
  },
  get clientId() {
    return raw.clientId;
  },
  get userPoolId() {
    return raw.userPoolId;
  },
  get identityProvider() {
    return raw.identityProvider;
  },
  get issuer() {
    return issuerFor(raw.userPoolId);
  },
  get authorizeUrl() {
    return `https://${raw.domain}/oauth2/authorize`;
  },
  get tokenUrl() {
    return `https://${raw.domain}/oauth2/token`;
  },
  get logoutUrl() {
    return `https://${raw.domain}/logout`;
  },
};

export default authConfig;
